import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon, SparklesIcon, ArrowPathIcon, InformationCircleIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../../../context/AuthContext';
import UrlConfig from '../../../data/UrlConfig';

// --- (El resto de las constantes y el componente ActivitySummary no cambian) ---

const FUTURE_LOOK_BACK_AREAS_DATA = [
    { id: 'love-relationship', title: "Tu Relación Amorosa", prompt: "Ha sido tu mejor año en el amor. ¿Cómo te mira tu pareja por la mañana? ¿Qué tipo de citas y conversaciones tienen? Describe la conexión profunda y la alegría que comparten." },
    { id: 'friendships', title: "Tus Amistades", prompt: "Tus amistades te llenan de energía. ¿Con quiénes pasas tiempo? ¿Qué actividades hacen juntos? Describe cómo se siente tener un círculo social que te apoya y te eleva." },
    { id: 'finances', title: "Tus Finanzas", prompt: "Ha sido tu mejor año financiero. ¿Cuánto dinero estás ganando? ¿Cuánto has ahorrado? ¿A quién estás ayudando financieramente? Describe cómo se ve y se siente este éxito." },
    { id: 'quality-of-life', title: "Tu Calidad de Vida", prompt: "Vives en tu entorno ideal. ¿Cómo es tu casa? ¿Tu lugar de trabajo? Describe los detalles, los olores, los sonidos y la sensación de paz y abundancia que te rodea." },
    { id: 'health-fitness', title: "Tu Salud y Estado Físico", prompt: "Te sientes más fuerte y vital que nunca. ¿Qué hábitos de ejercicio y alimentación tienes? ¿Cómo se siente despertar cada día lleno de energía y sin malestares?" },
    { id: 'intellectual-life', title: "Tu Vida Intelectual", prompt: "Tu mente está en llamas. ¿Qué libros has leído, qué cursos has tomado? Describe la emoción de aprender y la confianza que tienes en tu capacidad para resolver problemas." },
    { id: 'emotional-life', title: "Tu Vida Emocional", prompt: "Dominas tu mundo emocional. ¿Cómo manejas el estrés? ¿Qué emociones predominan en tu día a día? Describe tu estado de calma, felicidad y resiliencia." },
    { id: 'spiritual-life', title: "Tu Vida Espiritual", prompt: "Sientes una profunda conexión. ¿Cómo es tu práctica espiritual diaria? Describe la paz interior y el sentido de propósito que guían tus acciones." },
    { id: 'career', title: "Tu Carrera", prompt: "Tu carrera es emocionante y satisfactoria. ¿Has ascendido, empezado tu propia empresa, o llevado tu proyecto al siguiente nivel? ¿Eres el jefe? Describe tus logros y el impacto que estás creando." },
    { id: 'character', title: "Tu Carácter", prompt: "Eres la persona que siempre quisiste ser. ¿Cómo te ven los demás? ¿Qué valores como la honestidad, la compasión y la generosidad demuestras a diario?" },
    { id: 'family-parenting', title: "Tu Familia y Paternidad", prompt: "Tus relaciones familiares son armoniosas. ¿Cómo es la dinámica con tus hijos, padres o parientes cercanos? Describe los momentos de conexión y alegría que comparten." },
    { id: 'life-vision', title: "Tu Visión de Vida", prompt: "Tienes una claridad absoluta. Mirando hacia atrás desde tu futuro exitoso, ¿qué decisiones clave tomaste? ¿Qué dejaste ir? Describe la persona en la que te has convertido." }
];

const FINAL_SUMMARY = {
    title: "¡Tu Futuro Ideal está Trazado!",
    text: "Has creado una visión clara y emocionante para el próximo año. Este no es solo un sueño; es tu nuevo punto de destino. Ahora tienes un 'filtro' interno que te ayudará a tomar decisiones alineadas con esta visión.",
    points: [
        { title: "El Poder del Filtro", text: "Usa esta visión para decir 'no' a lo que no te acerca a tu meta y 'sí' a las oportunidades que resuenan con tu futuro ideal." },
        { title: "De la Visión a la Acción", text: "Revisa estas notas a menudo. La claridad que has ganado es el primer paso para tomar acciones inspiradas que conviertan esta visión en tu realidad." }
    ]
};

const ActivitySummary = ({ onRedo, nextActivity, onProceed }) => (
    <div className="bg-gray-900/50 p-8 rounded-xl border border-yellow-500/30 text-center animate-fade-in">
        <CheckCircleIcon className="w-16 h-16 text-green-400 mx-auto mb-4" />
        <h2 className="text-3xl font-bold mb-4">{FINAL_SUMMARY.title}</h2>
        <p className="text-gray-300 mb-8 max-w-2xl mx-auto">{FINAL_SUMMARY.text}</p>
        <div className="space-y-6 text-left max-w-2xl mx-auto">
            {FINAL_SUMMARY.points.map(q => (
                <div key={q.title} className="bg-gray-800 p-4 rounded-lg flex items-start gap-4">
                    <SparklesIcon className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1"/>
                    <div>
                        <h3 className="font-bold text-yellow-400 text-lg">{q.title}</h3>
                        <p className="text-gray-300 mt-1">{q.text}</p>
                    </div>
                </div>
            ))}
        </div>
        <div className="mt-10 flex flex-col sm:flex-row justify-center items-center gap-4">
            <button onClick={onRedo} className="flex items-center justify-center gap-2 w-full sm:w-auto bg-gray-700 text-gray-300 font-bold py-3 px-8 rounded-lg hover:bg-gray-600 transition-all duration-300">
                <ArrowPathIcon className="w-5 h-5"/>
                Revisar mi Visión
            </button>
            {nextActivity && (
                <button onClick={onProceed} className="w-full sm:w-auto inline-flex items-center gap-2 bg-yellow-500 text-black font-bold py-3 px-8 rounded-lg hover:bg-yellow-400 transition-colors">
                    Siguiente Actividad
                    <ArrowRightIcon className="w-5 h-5"/>
                </button>
            )}
        </div>
        <div className="text-center mt-6">
            <Link to="/cursos/manifiesta" className="text-sm text-gray-500 hover:text-yellow-400 transition-colors">Volver al Temario</Link>
        </div>
    </div>
);


const FutureLookBackActivity = ({ nextActivity, onProceed }) => {
    const { user, getGuestActivityData, saveGuestActivityProgress } = useAuth();
    const activityId = 'viendo-hacia-atras-desde-el-futuro';

    // Estados y useEffects no cambian
    const [responses, setResponses] = useState({});
    const [currentAreaIndex, setCurrentAreaIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [isActivityCompleted, setIsActivityCompleted] = useState(false);
    const [sortedAreas, setSortedAreas] = useState([]);
    const textareaRef = useRef(null);
    const currentArea = useMemo(() => sortedAreas[currentAreaIndex], [sortedAreas, currentAreaIndex]);

    useEffect(() => {
        if (textareaRef.current && !isActivityCompleted) textareaRef.current.focus();
    }, [currentAreaIndex, isActivityCompleted, sortedAreas]);

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            setError(null);
            if (user) {
                try {
                    const response = await fetch(`${UrlConfig.activity_getAreaDefinitions}&activityId=${activityId}`, { credentials: 'include' });
                    if (!response.ok) throw new Error('No se pudieron cargar los datos.');
                    const result = await response.json();
                    if (result.success) {
                        const { definitions: loadedResponses = {}, ratings: loadedRatings = {} } = result.data;
                        setResponses(loadedResponses);
                        const mergedAreas = FUTURE_LOOK_BACK_AREAS_DATA.map(area => ({ ...area, score: loadedRatings[area.id] ?? 11 }));
                        mergedAreas.sort((a, b) => a.score - b.score);
                        setSortedAreas(mergedAreas);
                        if (FUTURE_LOOK_BACK_AREAS_DATA.every(area => loadedResponses[area.id]?.trim())) setIsActivityCompleted(true);
                    } else { throw new Error(result.message); }
                } catch (err) {
                    setError("Error de conexión. Revisa tu conexión e inténtalo de nuevo.");
                    setSortedAreas(FUTURE_LOOK_BACK_AREAS_DATA);
                }
            } else {
                const guestResponses = getGuestActivityData(activityId) || {};
                setResponses(guestResponses); setSortedAreas(FUTURE_LOOK_BACK_AREAS_DATA);
                if (Object.keys(guestResponses).length >= FUTURE_LOOK_BACK_AREAS_DATA.length) setIsActivityCompleted(true);
            }
            setIsLoading(false);
        };
        loadData();
    }, [user, activityId, getGuestActivityData]);

    // Funciones de manejo de eventos (sin cambios)
    const handleTextChange = (e) => setResponses(prev => ({ ...prev, [currentArea.id]: e.target.value }));
    const saveCurrentResponse = async () => { if (!user || !currentArea || !responses[currentArea.id]) return; setIsSaving(true); setError(null); try { const response = await fetch(UrlConfig.activity_saveAreaDefinition, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ activityId, areaId: currentArea.id, definition: responses[currentArea.id] || '' }) }); const result = await response.json(); if (!result.success) throw new Error(result.message || 'Error al guardar.'); } catch (err) { setError(err.message); } finally { setIsSaving(false); } };
    const markActivityAsComplete = async () => { if (user) { try { await fetch(UrlConfig.activity_markAsComplete, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ activityId }) }); } catch (error) { console.error("Error al marcar como completada:", error); } } else { saveGuestActivityProgress(activityId, responses); } };
    const goToNext = async () => { await saveCurrentResponse(); if (currentAreaIndex === sortedAreas.length - 1) { await markActivityAsComplete(); setIsActivityCompleted(true); } else if (currentAreaIndex < sortedAreas.length - 1) { setCurrentAreaIndex(prev => prev + 1); } };
    const goToPrev = () => { if (currentAreaIndex > 0) setCurrentAreaIndex(prev => prev - 1); };
    const handleRedoActivity = () => { setIsActivityCompleted(false); setCurrentAreaIndex(0); };
    const handleProceed = async () => { await saveCurrentResponse(); await markActivityAsComplete(); setIsActivityCompleted(true); };

    // Lógica de renderizado
    if (isLoading) return <p className="text-center text-yellow-400">Cargando tu visión del futuro...</p>;
    if (error && user) return <p className="text-center text-red-500">{error}</p>;
    if (sortedAreas.length === 0) return <p className="text-center text-yellow-400">Preparando actividad...</p>;
    if (isActivityCompleted) return <ActivitySummary onRedo={handleRedoActivity} nextActivity={nextActivity} onProceed={onProceed} />;

    return (
        <div>
            {/* --- CAMBIO: Condición mejorada y texto personalizado --- */}
            {user && currentAreaIndex === 0 && !responses[sortedAreas[0]?.id]?.trim() && (
                <div className="mb-6 p-4 rounded-lg bg-gray-800 border border-yellow-500/20 flex items-start gap-4 animate-fade-in">
                    <InformationCircleIcon className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                    <div>
                        <h3 className="font-bold text-yellow-400">Prioridades para tu Visión</h3>
                        <p className="text-gray-300 mt-1 text-sm">
                            Para ayudarte a construir la visión más poderosa, hemos priorizado las áreas donde un cambio positivo tendrá el mayor impacto en tu vida.
                        </p>
                    </div>
                </div>
            )}

          <div className="mb-4">
              <p className="text-yellow-400 font-bold text-sm tracking-wider">ÁREA DE ENFOQUE {currentAreaIndex + 1} DE {sortedAreas.length}</p>
              {/* Contenedor Flex para alinear título y rating */}
              <div className="flex items-center gap-3 mt-1">
                  <h2 className="text-3xl md:text-4xl font-bold">{currentArea.title}</h2>
                  {/* --- CAMBIO: Insignia para mostrar el rating --- */}
                  {/* Solo se muestra si el score es válido */}
                  {currentArea.score <= 10 && (
                      <span className="animate-fade-in flex-shrink-0 bg-purple-900/60 text-purple-300 text-lg font-bold px-3 py-1 rounded-full border border-purple-500/50">
                          {currentArea.score}
                      </span>
                  )}
              </div>
          </div>
            <div className="bg-gray-900/50 p-6 rounded-lg border border-yellow-500/20">
                <p className="text-gray-300 mb-4 italic">{currentArea.prompt}</p>
                <textarea ref={textareaRef} className="w-full h-48 p-4 bg-gray-900 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition" placeholder="Escribe libremente, sin restricciones ni dudas. Siente la emoción de que esto ya es real..." value={responses[currentArea.id] || ''} onChange={handleTextChange} />
            </div>
            {error && <p className="text-red-500 mt-2 text-sm">{error}</p>}
            
            <div className="mt-8 flex justify-between items-center">
                <button onClick={goToPrev} disabled={currentAreaIndex === 0 || isSaving} className="flex items-center gap-2 px-4 py-2 bg-gray-700 rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    <ArrowLeftIcon className="w-5 h-5" /> Anterior
                </button>
                <button onClick={goToNext} disabled={isSaving || !responses[currentArea.id]} className="flex items-center gap-2 px-6 py-3 bg-yellow-500 text-black font-bold rounded-lg hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    {isSaving ? 'Guardando...' : (currentAreaIndex === sortedAreas.length - 1 ? 'Finalizar y Trazar Futuro' : 'Guardar y Continuar')}
                    {!isSaving && <ArrowRightIcon className="w-5 h-5" />}
                </button>
            </div>
            
            {currentAreaIndex >= 3 && !isActivityCompleted && (
                <div className="text-center mt-6">
                    <p className="text-gray-400 text-sm mb-2">Has definido la visión para tus áreas clave. Puedes continuar o finalizar.</p>
                    <button onClick={handleProceed} disabled={isSaving} className="text-yellow-500 hover:text-yellow-400 font-bold underline transition-colors disabled:opacity-50">
                        Finalizar ahora y ver el resumen
                    </button>
                </div>
            )}
        </div>
    );
};

export default FutureLookBackActivity;